import { CountryOption, LanguageOption } from 'models/basicData';
import {
  Address,
  Customer,
  CustomerConsentTranslationText,
  CustomerType,
  CustomerUpdateForm,
  EContact,
  ICustomerUpdate,
  ParsedPrimaryContact
} from 'models/customer';
import { PhoneNumber } from 'models/phoneNumber';
import { ProductRegistrationInfo } from 'models/productRegistration';

export function buildAddress(address?: Address): string {
  let addressLine = '—';
  if (!address?.street) {
    return addressLine;
  }

  addressLine = address.street;

  if (address.zipCode) {
    addressLine += `, ${address.zipCode}`;
  }
  if (address.state) {
    addressLine += `, ${address.state}`;
  }
  if (address.city) {
    addressLine += `, ${address.city}`;
  }
  return addressLine;
}

export function getCustomerPrimaryContactName(
  customer: Customer | null
): string {
  if (!customer?.attributes.primaryContact) {
    return '—';
  }
  const { firstName, lastName } = customer.attributes.primaryContact;
  return `${firstName} ${lastName}`;
}

export function findCustomerEmail(eContact?: EContact): string | null {
  if (!eContact?.emailContacts.length) {
    return null;
  }
  return eContact.emailContacts[0].emailAddress || null;
}

export function getCustomerEmail(eContact?: EContact): string {
  return findCustomerEmail(eContact) ?? '-';
}

export function getCustomerPhone(eContact?: EContact): string {
  if (eContact?.phoneContacts.length) {
    // The last phone number is the most recent one
    return eContact.phoneContacts.at(-1)?.phoneNumber || '';
  }
  return '';
}

export function getCustomerPhoneNumber(phoneNumber?: PhoneNumber): string {
  if (phoneNumber) {
    const { number = '', dialCode = '' } = phoneNumber;

    if (number || dialCode) {
      return `${dialCode}${number}`;
    }
  }
  return '—';
}

export function findCustomerOption(
  options?: CountryOption[] | LanguageOption[],
  property?: string
): string {
  if (options) {
    const found = options.find(({ value }) => value === property);
    if (found) {
      return found.label;
    }
  }
  return '—';
}

export function createCustomerUpdateRequest(
  values: CustomerUpdateForm,
  type: CustomerType
): ICustomerUpdate {
  const { country } = values;

  // Location address
  const locationAddress = {
    ...values.locationAddress,
    country
  };

  // Billing address
  let billingAddress = {
    ...values.billingAddress,
    country
  };

  // Billing same as location
  if (values.sameAsLocationAddress) {
    billingAddress = locationAddress;
  }

  const dialCode = (values.phoneNumber && values.dialCode) ?? '';
  const phoneNumber = values.phoneNumber ?? '';

  const primaryContactPhone = (() => {
    const pc = values.primaryContact;
    if (!pc) return '';
    const pn = pc.phoneNumber as unknown;
    if (typeof pn === 'string') {
      return pn || '';
    }
    return PhoneNumber.toString(pn as any) || '';
  })();

  const includeEmailInPrimaryContact =
    values.phoneNumber === '' && values.dialCode === '';

  const primaryContact: Partial<ParsedPrimaryContact> = {
    firstName: values.primaryContact?.firstName ?? '',
    lastName: values.primaryContact?.lastName ?? '',
    ...(includeEmailInPrimaryContact && {
      // Include email when main phone fields are empty (per test expectations)
      emailAddress: values.primaryContact?.emailAddress ?? ''
    }),
    phoneNumber: primaryContactPhone
  };

  return {
    firstName: values.firstName,
    lastName: values.lastName,
    locationAddress,
    billingAddress,
    country: values.country,
    language: values.language,
    type,
    eContact: {
      phoneContacts: [
        {
          phoneNumber: `${dialCode}${phoneNumber}`,
          isVerified: false
        }
      ]
    },
    primaryContact
  };
}

export function isBusinessCustomer(customer: Customer | null) {
  if (customer) {
    return customer.attributes.type === CustomerType.OrganizationCustomer;
  }
}

export function getConsentTranslation(
  texts?: CustomerConsentTranslationText,
  languageCode?: string
) {
  if (languageCode && texts?.[languageCode]) {
    return {
      title: texts[languageCode].title,
      description: texts[languageCode].text
    };
  }
  if (texts?.en) {
    return {
      title: texts.en.title,
      description: texts.en.text
    };
  }
  return {
    title: '',
    description: ''
  };
}

export function getCustomerSignupProductMeta(
  productInfo: ProductRegistrationInfo | null
) {
  return {
    iprId: productInfo?.iprId ?? null
  };
}
